import { type ReactNode, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { List } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { Combobox, type ComboboxOption } from '@/components/Combobox/Combobox';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/Select/Select';
import {
    type AppSessionPerson,
    type AppUsageParams,
    type AppUsageReport,
} from '@/features/reports/api';
import { sessionsCsv } from '@/features/reports/app/sessionsCsv';
import { SessionsTable } from '@/features/reports/app/SessionsTable';
import {
    APP_SESSION_ACTIONS,
    APP_SESSIONS_COPY,
    APP_USAGE_COPY as COPY,
} from '@/features/reports/copy';
import { ReportCard } from '@/features/reports/parts';
import {
    appSessionPeopleQuery,
    appSessionsQuery,
    appSessionsWindowQuery,
    reportsKeys,
} from '@/features/reports/queries';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { Register } from '@/layouts/Register';
import { csvFileName, downloadCsv } from '@/lib/csv';

const ANY = '__any__';

/**
 * The flat log the app version log always was (ALS-39), folded inside App usage (ALS-160): the
 * window's latest six under the cards, because everything above is built from it and support
 * still matches an IP or a version against a ticket. Show all opens the whole window as the paged,
 * searchable register the log page was; Export writes every row in the window as the same CSV.
 */
export function SessionLogCard({
    report,
    params,
}: {
    report: AppUsageReport;
    params: AppUsageParams;
}) {
    const [expanded, setExpanded] = useState(false);
    const queryClient = useQueryClient();
    const total = report.sessions;
    const latest = report.latestSessions;
    const folds = total > latest.length;

    // Still offered once expanded, even if the window has since shrunk to six or fewer, so the
    // reader can always fold the log back.
    const toggle =
        folds || expanded ? (
            <Button variant="outline" size="sm" onClick={() => setExpanded((open) => !open)}>
                {expanded ? COPY.log.showLatest(latest.length) : COPY.log.showAll(total)}
            </Button>
        ) : null;

    const exportWindow = async () => {
        const rows = await queryClient.fetchQuery(appSessionsWindowQuery(params));
        const csv = sessionsCsv(rows);
        downloadCsv(csvFileName('app-sessions'), csv.header, csv.rows);
    };

    const actions = (
        <>
            {toggle}
            <ExportButton save={exportWindow} disabled={total === 0} />
        </>
    );

    if (expanded) {
        // Keyed on the window and manager: a new scope starts again from page one with no filters,
        // rather than asking for a page or a person the new scope does not have.
        return (
            <SessionRegister
                key={`${params.days}|${params.rmId}`}
                params={params}
                actions={actions}
            />
        );
    }

    return (
        <ReportCard
            title={COPY.log.title}
            subtitle={folds ? COPY.log.latest(latest.length, total) : COPY.log.count(total)}
            actions={actions}
        >
            <p className="text-muted-foreground mb-3 text-sm">{COPY.log.note}</p>
            {total === 0 ? (
                <EmptyState
                    icon={<List />}
                    title={COPY.log.empty.title}
                    body={COPY.log.empty.body}
                />
            ) : (
                <SessionsTable rows={latest} />
            )}
        </ReportCard>
    );
}

/**
 * The whole window, paged on the server, with the log's own search, person and action filters:
 * what /app-version-log was, now scoped to App usage's window and relationship manager.
 */
function SessionRegister({ params, actions }: { params: AppUsageParams; actions: ReactNode }) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [person, setPerson] = useState<string | null>(null);
    const [action, setAction] = useState<string>(ANY);
    const [page, setPage] = useState(1);
    // Null until the register has measured how many rows fit. Nothing is fetched until it has,
    // so the screen asks for one page of the right size instead of a guess and two corrections.
    const [size, setSize] = useState<number | null>(null);

    const filters = {
        page,
        size: size ?? 0,
        q: search,
        userId: person === null ? null : Number(person),
        action: action === ANY ? null : action,
        days: params.days,
        rmId: params.rmId,
    };
    const sessions = useQuery(appSessionsQuery(filters, size !== null));
    const people = useQuery(appSessionPeopleQuery(params));

    // Every change to what is being asked for starts again from page one.
    const narrow =
        <T,>(set: (value: T) => void) =>
        (value: T) => {
            set(value);
            setPage(1);
        };

    const data = sessions.data;

    return (
        <Register
            title={COPY.log.title}
            actions={actions}
            search={search}
            onSearchChange={narrow(setSearch)}
            searchLabel={APP_SESSIONS_COPY.search}
            onReset={() => {
                setSearch('');
                setPerson(null);
                setAction(ANY);
                setPage(1);
            }}
            filters={
                <>
                    {/* A Combobox rather than a Select: 235 people is too many to scroll, and 47 of
                        them share a name with somebody else, so the list has to be searchable and
                        each entry has to carry the email that tells two of them apart. */}
                    <Combobox
                        value={person}
                        onValueChange={narrow(setPerson)}
                        options={peopleOptions(people.data)}
                        placeholder={APP_SESSIONS_COPY.everyone}
                        searchPlaceholder={APP_SESSIONS_COPY.findPerson}
                        emptyText={APP_SESSIONS_COPY.noPerson}
                        className="w-full sm:w-[220px]"
                        aria-label={APP_SESSIONS_COPY.person}
                    />
                    <Select value={action} onValueChange={narrow(setAction)}>
                        <SelectTrigger
                            className="sm:w-[160px]"
                            aria-label={APP_SESSIONS_COPY.action}
                        >
                            <SelectValue placeholder={APP_SESSIONS_COPY.anyAction} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ANY}>{APP_SESSIONS_COPY.anyAction}</SelectItem>
                            {APP_SESSION_ACTIONS.map((one) => (
                                <SelectItem key={one} value={one}>
                                    {one}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </>
            }
            status={sessions.isPending ? 'pending' : sessions.isError ? 'error' : 'ready'}
            onRetry={() =>
                void queryClient.invalidateQueries({ queryKey: reportsKeys.appSessions(filters) })
            }
            rows={data?.rows ?? []}
            total={data?.total ?? 0}
            pageKey={`${search}|${person ?? ANY}|${action}`}
            noMatch={APP_SESSIONS_COPY.noMatch}
            paging={{
                page,
                onPageChange: setPage,
                matched: data?.matched ?? 0,
                onPageSizeChange: setSize,
            }}
        >
            {(rows) => <SessionsTable rows={rows} />}
        </Register>
    );
}

/**
 * The people who appear in the log, as the filter offers them. The email rides along as the
 * option's description: it is what the reader picks between when two entries read the same name,
 * and the Combobox searches it as well as the name, so an address finds its owner directly.
 */
function peopleOptions(people: AppSessionPerson[] | undefined): ComboboxOption[] {
    return (people ?? []).map((one) => ({
        value: String(one.userId),
        label: one.name,
        description: one.email ?? undefined,
    }));
}
